// ============================================================
// ELC Shift — Uploads / Clock Review module (v102.0)
//
// Admin clock-log review + edit surface. Shows historical clock-in/out
// records with filters (date range, store, staff), allows admins to
// edit individual records (with audit trail).
//
// Public surface:
//   • renderClockLog (async)    — the main grid; fetches + renders
//   • openClockEditModal        — open per-record edit form
//   • closeClockEditModal       — close edit form
//   • saveClockEdit (async)     — persist edit + write audit_log entry
//
// Distinct from Uploads.clock (Sprint 7 — clock-in/out + break tracking
// for live shifts). This module is read-mostly review of past records.
//
// Free globals consumed:
//   DB, showToast, ALL_STORES, staffData, escapeHtml, formatDateShort,
//   localDateStr (Dubai-UTC+4-aware), businessDayStr (3am-3am boundary)
// ============================================================

// v136.8 (16 Oct audit) - the house helpers, never a hand-rolled Monday table: shiftDateStr walks forward from
// whatever weekday shifts.week_start really is (Monday in the UAE, SUNDAY in Qatar: docs/field-lessons.md
// "The Monday assumption"); clockOutDate is the till's own overnight rule (src/shared/clock-rules.js).
import { shiftDateStr } from '../worker/balances-calc.js';
import { clockOutDate, MAX_OPEN_SHIFT_HOURS } from '../shared/clock-rules.js';
import { coverAwareAreaStores } from '../shared/area-cover.js';   // v136.9: own area plus any area handed over for today

// v130.14 — the rostered-but-never-clocked finder (spec: an area manager's 18 Aug
// report). Pure-ish: takes the already-grouped clock sessions + the filters,
// fetches the rota for the weeks in view, returns synthetic rows.
//
// Bounded on purpose: it needs a start AND end date and covers at most 6
// weeks, so an unbounded filter can never fan out into dozens of rota reads.
// Future dates are skipped — a shift that hasn't happened isn't a no-show.
const NOSHOW_MAX_WEEKS = 6;

// v136.8 (16 Oct audit) - the store each displayed clock session's EXISTING row records, keyed
// staffId_date_seq, filled by renderClockLog. An amendment writes it back unchanged: it used to write the
// person's HOME store (s.store), so amending a cover or transfer-day shift moved its hours to the wrong store
// (the v131.17 rule: a clock row's store is where the work happened). Kept here, not in the onclick string, so
// a store name never has to be escaped into HTML. Module-private.
const _rowStoreBySession = {};
let _clockEditingStore = null;
// v136.33: the rota findRosteredNoShows reads, by staff_id|date -> [shift rows], kept for the table's Scheduled and
// Variance columns. They read a `shifts` global the admin app never fills, so they showed "—" on every recorded
// session; a split shift's second session is matched to its own leg (the one nearest its clock-in).
let _rotaLegs = new Map();
export function rotaLegFor(legs, seq, clockIn) {
  const list = (Array.isArray(legs) ? legs : []).filter((r) => r && r.start_time);
  if (!list.length) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(clockIn || ''));
  if (m) {
    const at = Number(m[1]) * 60 + Number(m[2]);
    const mins = (t) => { const x = /^(\d{1,2}):(\d{2})/.exec(String(t)); return x ? Number(x[1]) * 60 + Number(x[2]) : 0; };
    return list.reduce((b, r) => (Math.abs(mins(r.start_time) - at) < Math.abs(mins(b.start_time) - at) ? r : b), list[0]);
  }
  return list.find((r) => (r.seq || 1) === (seq || 1)) || list[0];
}

// The rota weeks to read for a date range: the Monday weeks (UAE rows) AND the Sunday weeks (Qatar rows) that
// cover it. Only Monday weeks were read, so a Qatar rota never loaded and a Qatar no-show was never flagged. A
// store filter of known country reads its own anchor only. Each anchor is capped at NOSHOW_MAX_WEEKS; null means
// too wide (the caller stays quiet, as before). Pure, exported for tests.
export function noShowWeekStarts(from, to, country) {
  const back = (d, toSunday) => {
    const x = new Date(d + 'T12:00:00Z');
    const n = toSunday ? x.getUTCDay() : (x.getUTCDay() + 6) % 7;
    x.setUTCDate(x.getUTCDate() - n);
    return x.toISOString().slice(0, 10);
  };
  const next = (d) => { const x = new Date(d + 'T12:00:00Z'); x.setUTCDate(x.getUTCDate() + 7); return x.toISOString().slice(0, 10); };
  const anchors = country === 'ELC - Qatar' ? [true] : country === 'ELC - UAE' ? [false] : [false, true];
  const out = [];
  for (const sun of anchors) {
    let n = 0;
    for (let w = back(from, sun); w <= back(to, sun); w = next(w)) {
      out.push(w);
      if (++n > NOSHOW_MAX_WEEKS) return null;
    }
  }
  return out;
}
export async function findRosteredNoShows({ grouped, staffById, storeFilter, dateFrom, dateTo }) {
  _rotaLegs = new Map();   // v136.33: never a previous view's rota
  const from = dateFrom || dateTo;
  const to = dateTo || dateFrom;
  if (!from || !to) return [];                       // unbounded view — skip
  const today = todayDateStr();
  const endDate = to > today ? today : to;           // never flag the future
  if (from > endDate) return [];

  // v136.8: the Monday weeks (UAE) AND the Sunday weeks (Qatar) covering the range; a filtered store reads only
  // its own country's anchor. Was Mondays only, so Qatar weeks never loaded.
  let country = null;
  try { if (storeFilter && typeof getStoreCountry === 'function') country = getStoreCountry(storeFilter); } catch (e) { country = null; }
  const weeks = noShowWeekStarts(from, endDate, country);
  if (!weeks) return [];                             // too wide — stay quiet

  // Which staff are in scope (same rules the table already applies).
  const amStores = (currentAdmin.role === 'area_manager' && typeof AREA_STORE_MAP !== 'undefined')
    ? coverAwareAreaStores(currentAdmin, AREA_STORE_MAP) : null;   // v136.9: + any area handed over for today
  const inScope = (s) => {
    if (!s || s.active === false) return false;
    if (storeFilter && s.store !== storeFilter) return false;
    if (amStores && !amStores.includes(s.store)) return false;
    return true;
  };

  const rota = [];
  for (const wk of weeks) {
    const rows = await DB.shifts.list({ week: wk, store: storeFilter || undefined }).catch(() => []);
    (Array.isArray(rows) ? rows : []).forEach((r) => rota.push(r));
  }
  if (!rota.length) return [];
  // v136.33: kept for the Scheduled and Variance columns (every working leg, both legs of a split shift)
  rota.forEach((r) => {
    if (!r || r.type !== 'work' || !r.week_start || !r.day) return;
    const k = r.staff_id + '|' + shiftDateStr(String(r.week_start).slice(0, 10), r.day);
    if (!_rotaLegs.has(k)) _rotaLegs.set(k, []);
    _rotaLegs.get(k).push(r);
  });

  const DAY_ABBR = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const out = [];
  rota.forEach((r) => {
    // Only a rostered WORK shift can be a no-show. off/holiday/sick already
    // explain the day, and a leg-2 row is covered by its own seq session.
    if (!r || r.type !== 'work' || (r.seq || 1) !== 1) return;
    const s = staffById[r.staff_id];
    if (!inScope(s)) return;
    // The calendar date of this rota cell.
    const idx = DAY_ABBR.indexOf(r.day);
    if (idx === -1) return;
    // v136.8: from the row's OWN week start (was "week runs Mon..Sun", a day early for every Qatar row).
    const dateStr = shiftDateStr(String(r.week_start).slice(0, 10), r.day);
    if (dateStr < from || dateStr > endDate) return;
    // Already has a clock session that day? Then it is not a no-show.
    const clocked = Object.values(grouped).some((g) => g.staff && g.staff.id === r.staff_id && g.date === dateStr);
    if (clocked) return;
    out.push({
      staff: s, date: dateStr, store: r.work_store || s.store,
      clockIn: null, clockOut: null, breakMins: 0, breakStart: null, ids: [],
      seq: 1, rowId: null, noClock: true,
      schedIn: r.start_time || '', schedOut: r.end_time || '',
      lat: null, lng: null, locAccuracy: null, locLabel: null, locDenied: false,
    });
  });
  return out;
}

export async function renderClockLog() {
  const tbody = document.getElementById('clockLogBody');
  const storeFilter = document.getElementById('clockLogStore')?.value || '';
  const dateFrom  = document.getElementById('clockLogDateFrom')?.value || '';
  const dateTo    = document.getElementById('clockLogDate')?.value  || '';

  const canEdit = currentAdmin.role === 'admin' || currentAdmin.role === 'area_manager';

  // Show loading state
  tbody.innerHTML = '<tr><td colspan="10" style="text-align:center;padding:24px;color:var(--sub)">Loading clock events…</td></tr>';

  // Fetch from DB. If only one date given, use it; if range, fetch without date filter and filter client-side.
  let dbRows = [];
  try {
    const params = {};
    if (storeFilter) params.store = storeFilter;
    // If from === to, pass single date param (worker filters server-side — fastest)
    if (dateFrom && dateTo && dateFrom === dateTo) {
      params.date = dateFrom;
    } else {
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
    }
    dbRows = await DB.clock.listAdmin(params) || [];
  } catch(e) {
    console.warn('[DB] Clock log load failed:', e.message);
    tbody.innerHTML = '<tr><td colspan="10" style="text-align:center;padding:24px;color:var(--danger)">Failed to load clock events. ' + (e.message||'') + '</td></tr>';
    return;
  }

  // Client-side filtering for range, area manager scope, and store (if not already server-filtered)
  const rows = [];
  const csvRows = [];
  const staffById = {};
  staffData.forEach(s => { staffById[s.id] = s; });

  // Group rows by staff+date so a clock-in + clock-out on separate DB rows merge into one table row
  const grouped = {};
  Object.keys(_rowStoreBySession).forEach((k) => { delete _rowStoreBySession[k]; });   // v136.8: this render's rows only
  dbRows.forEach(r => {
    const s = staffById[r.staff_id];
    if (!s) return; // orphan row — staff deleted

    // Scope filtering
    if (storeFilter && r.store !== storeFilter && s.store !== storeFilter) return;
    // v129.11 (the Qatar AM four): scope AMs by their STORE LIST, not the
    // fragile per-row area field — a staff row without area_id (the Amanah
    // apply never wrote one) vanished from this view for its own AM.
    if (currentAdmin.role === 'area_manager') {
      const amStores = (typeof AREA_STORE_MAP !== 'undefined') ? coverAwareAreaStores(currentAdmin, AREA_STORE_MAP) : [];   // v136.9: + hand-overs
      if (!amStores.includes(s.store) && !amStores.includes(r.store)) return;
    }

    // Date range filter (client-side when from !== to)
    if (dateFrom && r.date < dateFrom) return;
    if (dateTo && r.date > dateTo) return;

    // v130.13 — key by SESSION, not by day. Since v198-1 a day can hold more
    // than one clock session, and the old staff+date key merged them into a
    // phantom row (earliest in, latest out): a staff member's 17 Aug showed as one
    // 13:37-21:00 shift when it was really two rows, so the duplicate was
    // invisible and an amendment appeared to "not save" (it edited one row
    // while the display kept showing the merged pair). The staff clock card
    // was fixed for this in v129.158; the admin log never was.
    const key = r.staff_id + '_' + r.date + '_' + (r.seq || 1);
    const isFirstForKey = !grouped[key];
    if (isFirstForKey) {
      grouped[key] = { staff: s, date: r.date, store: r.store || s.store, clockIn: null, clockOut: null, breakMins: 0, breakStart: null, ids: [],
        seq: (r.seq || 1), rowId: r.id,   // v130.13 — which session this is, and the row to amend/remove
        lat: null, lng: null, locAccuracy: null, locLabel: null, locDenied: false };
      // v136.8: the store the EXISTING row records (where the work happened), so an amendment keeps it.
      _rowStoreBySession[key] = r.store || null;
    }
    grouped[key].ids.push(r.id);
    // First row seen (newest, since order=created_at.desc) is authoritative for location
    if (isFirstForKey) {
      if (r.lat != null) grouped[key].lat = r.lat;
      if (r.lng != null) grouped[key].lng = r.lng;
      if (r.location_accuracy_m != null) grouped[key].locAccuracy = r.location_accuracy_m;
      if (r.location_label) grouped[key].locLabel = r.location_label;
      if (r.location_denied) grouped[key].locDenied = true;
    } else {
      // Fallback: if first row had no location but a later row does, backfill
      if (grouped[key].lat == null && r.lat != null) {
        grouped[key].lat = r.lat;
        grouped[key].lng = r.lng;
        grouped[key].locAccuracy = r.location_accuracy_m;
        grouped[key].locLabel = r.location_label;
      }
      if (r.location_denied && !grouped[key].locLabel) grouped[key].locDenied = true;
    }
    // Extract HH:MM from timestamp
    const inT  = r.clock_in  ? String(r.clock_in).split('T')[1]?.slice(0,5)  || String(r.clock_in).slice(11,16)  : null;
    const outT = r.clock_out ? String(r.clock_out).split('T')[1]?.slice(0,5) || String(r.clock_out).slice(11,16) : null;
    const brkT = r.break_start ? String(r.break_start).split('T')[1]?.slice(0,5) || String(r.break_start).slice(11,16) : null;
    // Keep earliest clock_in, latest clock_out
    if (inT && (!grouped[key].clockIn || inT < grouped[key].clockIn))  grouped[key].clockIn  = inT;
    if (outT && (!grouped[key].clockOut || outT > grouped[key].clockOut)) grouped[key].clockOut = outT;
    // Largest break_mins wins (we rewrite on each break_end)
    if (r.break_mins && r.break_mins > grouped[key].breakMins) grouped[key].breakMins = r.break_mins;
    // break_start: only the MOST RECENT row (first seen, because order=created_at.desc) decides live break state.
    if (isFirstForKey) grouped[key].breakStart = brkT;
  });

  // Sort: newest date first, then name
  // ── v130.14 — WHO WAS ROSTERED BUT NEVER CLOCKED IN ────────────────────────
  // An area manager, 18 Aug: "To see [a staff member] from Springs appear even if he did not
  // clock in, for me to Edit. The admin shows only the staff who clocked in."
  // She was right: this table was built PURELY from clock rows, so a no-show
  // had no line — and a manager cannot amend a record that isn't displayed.
  // The people most needing a correction were the only ones invisible.
  //
  // So: pull the rota for the dates being viewed and add a row for every
  // rostered WORK shift with no clock session. Rota leave types (off/holiday/
  // sick) are NOT flagged — those are already an answer for the day.
  // Best-effort: if the rota can't be read the table renders exactly as
  // before, never an error.
  const noShows = await findRosteredNoShows({
    grouped, staffById, storeFilter, dateFrom, dateTo,
  }).catch(() => []);
  noShows.forEach((n) => { grouped['noshow_' + n.staff.id + '_' + n.date] = n; });

  // v130.13 — how many sessions each staff-day has, so a normal single-session
  // day looks exactly as it always did and only a genuine multi-session day
  // gets the session badge + per-session delete.
  const sessionCount = {};
  Object.values(grouped).forEach((g) => {
    const dk = g.staff.id + '_' + g.date;
    sessionCount[dk] = (sessionCount[dk] || 0) + 1;
  });
  const entries = Object.values(grouped).sort((a,b) => {
    if (a.date !== b.date) return b.date.localeCompare(a.date);
    return a.staff.name.localeCompare(b.staff.name);
  });

  entries.forEach(g => {
    const s = g.staff;
    // Find scheduled shift for this date to compute variance
    const dayAbbr = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(g.date + 'T12:00:00').getDay()];
    const wkStart = (function(d){ const x=new Date(d+'T12:00:00'); const day=x.getDay(); const diff=day===0?-6:1-day; x.setDate(x.getDate()+diff); return localDateStr(x); })(g.date);
    const shiftKey = wkStart + '_' + s.id + '_' + dayAbbr;
    const shift = (typeof shifts !== 'undefined' && shifts[shiftKey]) || null;
    // v130.14 — a no-show row already carries its rostered times (it was BUILT
    // from the rota), so it shows them even though the `shifts` global is not
    // populated in the admin app.
    // v136.33: the rota this view read (findRosteredNoShows), the leg for THIS session
    const leg = rotaLegFor(_rotaLegs.get(s.id + '|' + g.date), g.seq, g.clockIn);
    const schedIn  = g.schedIn || (leg ? String(leg.start_time).slice(0, 5) : '') || (shift && shift.start  ? shift.start  : '');
    const schedOut = g.schedOut || (leg && leg.end_time ? String(leg.end_time).slice(0, 5) : '') || (shift && shift.finish ? shift.finish : '');

    const clockInStr  = g.clockIn  || '—';
    const clockOutStr = g.clockOut || '—';
    // v130.14 — a rostered no-show: shown so it can be corrected, and marked
    // clearly so nobody mistakes it for a recorded shift.
    const noClock = !!g.noClock;

    // Break display: live "On break since HH:MM" takes priority, else total minutes
    let breakStr = '—', breakClass = 'color:var(--sub);font-size:11px';
    if (g.breakStart) {
      breakStr = 'On break since ' + g.breakStart;
      breakClass = 'color:#d97706;font-weight:600;font-size:12px';
    } else if (g.breakMins > 0) {
      breakStr = g.breakMins + ' min';
      breakClass = 'color:var(--sub);font-size:12px';
    }

    // Variance vs scheduled in (positive = late)
    let varStr = '—', varClass = 'color:var(--sub)';
    if (schedIn && g.clockIn) {
      const [sh,sm] = schedIn.split(':').map(Number);
      const [ch,cm] = g.clockIn.split(':').map(Number);
      const variance = (ch*60+cm) - (sh*60+sm);
      if (variance > 5)       { varStr = '+' + variance + 'm late';  varClass = 'color:var(--danger)'; }
      else if (variance < -2) { varStr = Math.abs(variance) + 'm early'; varClass = 'color:#10b981'; }
      else                    { varStr = 'On time'; varClass = 'color:var(--sub)'; }
    }

    // v130.13 — the edit carries the SESSION (seq), so an amendment updates
    // the row the manager is looking at instead of always session 1. A day
    // with more than one session also offers a per-session delete: before
    // this, the only delete removed the WHOLE day, so a manager facing one
    // spurious duplicate had no safe move.
    const multi = sessionCount[s.id + '_' + g.date] > 1;
    // v130.14 — on a no-show the action is to RECORD the day, not amend it.
    const editLabel = noClock ? 'Add times' : 'Edit';
    const editBtn = canEdit ? `<button class="btn btn-outline btn-sm"${noClock ? ' style="border-color:#0a7d4f;color:var(--ok)"' : ''} onclick="Uploads.clockReview.openClockEditModal(${s.id},'${g.date}','${clockInStr}','${clockOutStr}','${g.breakStart || ''}',${g.breakMins || 0},${g.seq || 1})">${editLabel}</button>` : '';
    const delBtn = (canEdit && multi && g.rowId)
      ? ` <button class="btn btn-outline btn-sm" style="border-color:#dc2626;color:var(--adm-red)" title="Remove this session only — the other session for this day stays" onclick="Uploads.clockReview.deleteClockSession(${Number(g.rowId)},${Number(s.id)},'${g.date}',${Number(g.seq) || 1})">Delete session</button>`
      : '';

    // Location cell — chip with label + accuracy, click opens Google Maps
    let locCell = '<span style="color:var(--sub);font-size:11px">—</span>';
    if (g.locDenied) {
      locCell = '<span title="Location permission denied or unavailable" style="display:inline-block;padding:2px 8px;background:var(--adm-danger-pale);border:1px solid var(--adm-danger-line);border-radius:10px;font-size:11px;color:var(--danger-deep);font-weight:600">🚫 Denied</span>';
    } else if (g.lat != null) {
      const label = g.locLabel ? escapeHtml(g.locLabel) : (g.lat.toFixed(4) + ', ' + g.lng.toFixed(4));   // v132.85: was quotes-only; < got through
      const acc = g.locAccuracy != null ? ' \u00B1' + g.locAccuracy + 'm' : '';
      const mapHref = 'https://www.google.com/maps?q=' + g.lat + ',' + g.lng;
      locCell = `<a href="${mapHref}" target="_blank" rel="noopener" title="${g.lat.toFixed(5)}, ${g.lng.toFixed(5)}${acc} — click to open in Google Maps" style="display:inline-block;padding:2px 8px;background:var(--adm-ok-pale);border:1px solid var(--adm-ok-line);border-radius:10px;font-size:11px;color:var(--ok-deep);font-weight:600;text-decoration:none;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;vertical-align:middle">📍 <span translate="no">${label}${acc}</span></a>`;
    }

    rows.push(`<tr${noClock ? ' style="background:var(--adm-warn-pale)"' : ''}>
      <td><strong translate="no">${escapeHtml((s.name||'').split(' ')[0])}</strong>${multi ? ` <span title="This day has more than one clock session" style="display:inline-block;padding:1px 6px;background:var(--adm-purple-pale);border:1px solid var(--adm-purple-line);border-radius:8px;font-size:10px;color:#3730a3;font-weight:700">session ${g.seq || 1}</span>` : ''}${noClock ? ` <span title="Rostered ${escapeHtml(schedIn)}-${escapeHtml(schedOut)} but never clocked in — nothing was recorded for this day" style="display:inline-block;padding:1px 6px;background:var(--warn-bg);border:1px solid var(--adm-warn-line);border-radius:8px;font-size:10px;color:var(--warn-deep);font-weight:700">no clock-in</span>` : ''}</td>
      <td style="font-size:11px;color:var(--sub)">${g.date}</td>
      <td style="font-weight:700;color:var(--ok-deep)">${clockInStr}</td>
      <td style="font-weight:700;color:${clockOutStr==='—'?'var(--danger)':'var(--danger-deep)'}">${clockOutStr}</td>
      <td style="${breakClass}">${breakStr}</td>
      <td style="color:var(--sub);font-size:12px">${schedIn&&schedOut?schedIn+'–'+schedOut:'—'}</td>
      <td style="${varClass};font-weight:600;font-size:12px">${varStr}</td>
      <td>${locCell}</td>
      <td style="font-size:11px;color:var(--sub)" translate="no">${(g.store||'').split(' - ').pop()}</td>
      <td>${editBtn}${delBtn}</td>
    </tr>`);

    csvRows.push({name: s.name, store: g.store||s.store, date: g.date,
      clockIn: clockInStr, clockOut: clockOutStr, breakMins: g.breakMins || 0,
      scheduled: schedIn&&schedOut?schedIn+'-'+schedOut:'', variance: varStr,
      location: g.locDenied ? 'DENIED' : (g.locLabel || (g.lat != null ? g.lat + ',' + g.lng : '')),
      lat: g.lat != null ? g.lat : '', lng: g.lng != null ? g.lng : '',
      accuracy_m: g.locAccuracy != null ? g.locAccuracy : '',
      // v130.14 — a no-show must be unmistakable in the export too: a blank
      // in/out row that looks like data would read as "worked, times missing".
      amended: noClock ? 'NO CLOCK-IN (rostered)' : 'No'});
  });

  window._clockLogCSV = csvRows;

  if (!rows.length) {
    tbody.innerHTML = '<tr><td colspan="10" style="text-align:center;padding:24px;color:var(--sub)">No clock events found for this filter.</td></tr>';
    return;
  }
  tbody.innerHTML = rows.slice(0, 200).join('');
}

export function openClockEditModal(staffId, dateStr, clockIn, clockOut, breakStart, breakMins, seq) {
  clockEditingStaffId = staffId;
  clockEditingDate    = dateStr;
  clockEditingSeq     = seq || 1;   // v130.13 — amend THIS session, not always the first
  // v136.8: the store the existing row records; null for a no-show (a NEW row: saveClockEdit uses the home store).
  _clockEditingStore  = _rowStoreBySession[staffId + '_' + dateStr + '_' + (seq || 1)] || null;
  const s = staffData.find(x => x.id === staffId);
  document.getElementById('clockEditSub').textContent = (s ? s.name : 'Staff') + ' — ' + dateStr +
    ((seq || 1) > 1 ? ' (session ' + seq + ')' : '');
  document.getElementById('ceClockIn').value     = clockIn  !== '—' ? clockIn  : '';
  document.getElementById('ceCockOut').value     = clockOut !== '—' ? clockOut : '';
  document.getElementById('ceBreakStart').value  = breakStart || '';
  document.getElementById('ceBreakMins').value   = (breakMins != null && breakMins !== 0) ? String(breakMins) : '';
  document.getElementById('ceReason').value      = '';
  document.getElementById('clockEditModal').classList.add('open');
}

// v130.13 — remove ONE spurious session from a multi-session day. Deliberately
// only offered when a day HAS more than one session: on a normal day the right
// tool is Edit, not delete. Confirm names the person, day and session so the
// manager cannot delete the wrong one by reflex; the Worker audits the row.
//
// Overnight fix (sensitive): the button used to carry the staff NAME inside
// onclick="...('name')", escaped for single quotes only, so a name with a "
// or a backslash broke the handler (the field-lessons rule: pass an id and
// look the value up). It now passes the staff id; the name is found here.
export async function deleteClockSession(rowId, staffId, dateStr, seq) {
  const person = (typeof staffData !== 'undefined' && Array.isArray(staffData))
    ? staffData.find(x => Number(x.id) === Number(staffId)) : null;
  const staffName = (person && person.name) || ('staff #' + staffId);
  const ok = (await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Delete session ' + seq + ' for ' + staffName + ' on ' + dateStr + '?\n\n' +
    'This removes ONE clock session. The other session for that day stays exactly as it is.\n\n' +
    'This is somebody\'s recorded hours — it is logged in the audit trail.', { danger: true, confirmLabel: 'Delete' }));
  if (!ok) return;
  try {
    await DB.clock.deleteOne(rowId);
    showToast('Session removed', 'success');
    renderClockLog();
  } catch (e) {
    showToast('Could not remove the session: ' + (e.message || 'unknown error'), 'error');
  }
}

export function closeClockEditModal() {
  document.getElementById('clockEditModal').classList.remove('open');
  clockEditingStaffId = null;
  clockEditingDate    = null;
}

// v127.23 — the amend form pinned clock-out to clock-in's calendar date, so an
// overnight shift (out after midnight) or an AM/PM slip silently wrote
// out-before-in rows (24771/24974/24122, corrected by SQL 25 Jul).
//
// v136.8 (16 Oct audit): the v127.23 rule only rolled an out time BEFORE 03:00,
// so a 22:00-07:00 overnight restock could not be amended at all (07:00 was
// "not after" 22:00). The amend screen now uses the SAME rule as the staff
// clock and the stuck-shift modal (clockOutDate, src/shared/clock-rules.js): a
// clock-out earlier than the clock-in is on the next day. The Worker's 24-hour
// check (clockRangeError) stays the safety net, and saveClockEdit asks before
// saving an overnight shift longer than MAX_OPEN_SHIFT_HOURS, which is where an
// AM/PM slip (09:00 in, 08:00 out) now gets caught.
// Pure — returns { ts, overnight } or { error }. Exported for tests.
export function resolveClockOutTs(dateStr, clockIn, clockOut) {
  if (!clockOut) return { ts: null };
  if (clockOut === clockIn) {
    return { error: 'Clock-out and clock-in are both ' + clockIn + ' — a zero-length shift is always a typo. Check the times.' };
  }
  const outDate = clockOutDate(dateStr, clockIn, clockOut);
  if (outDate === dateStr) return { ts: dateStr + 'T' + clockOut + ':00' };
  return { ts: outDate + 'T' + clockOut + ':00', overnight: true };
}

// v136.8: the length of an overnight in/out pair in hours (out is on the next day). Pure, exported for tests.
export function overnightHours(clockIn, clockOut) {
  const m = (t) => { const p = String(t || '').split(':').map(Number); return (p[0] || 0) * 60 + (p[1] || 0); };
  return Math.round(((24 * 60 - m(clockIn)) + m(clockOut)) / 6) / 10;
}

export async function saveClockEdit() {
  const clockIn    = document.getElementById('ceClockIn').value;
  const clockOut   = document.getElementById('ceCockOut').value;
  const breakStart = document.getElementById('ceBreakStart').value;
  const breakMinsRaw = document.getElementById('ceBreakMins').value;
  const reason     = document.getElementById('ceReason').value.trim();
  if (!clockIn)  { showToast('Clock-in time is required','error'); return; }
  if (!reason)   { showToast('Please enter a reason for this amendment','error'); return; }

  // Parse break minutes — empty → 0, anything non-numeric is rejected.
  const breakMins = breakMinsRaw === '' ? 0 : Number(breakMinsRaw);
  if (!Number.isFinite(breakMins) || breakMins < 0 || breakMins > 600) {
    showToast('Break minutes must be 0–600','error');
    return;
  }

  const staffId = clockEditingStaffId;
  const dateStr = clockEditingDate;
  const s = staffData.find(x => x.id === staffId);
  // v136.8: keep the store the clock row ALREADY records (where the work happened); the home store only for a
  // new row (a rostered no-show being recorded). It always wrote the home store, moving cover hours.
  const storeName = _clockEditingStore || (s ? s.store : '');

  // Build timestamps — match the format index.html uses (YYYY-MM-DDTHH:MM:SS, no timezone suffix)
  const clockInTs    = dateStr + 'T' + clockIn + ':00';
  // v127.23: overnight-aware — see resolveClockOutTs. Rejects AM/PM slips
  // instead of writing an impossible out-before-in row.
  const outResolved = resolveClockOutTs(dateStr, clockIn, clockOut);
  if (outResolved.error) { showToast(outResolved.error, 'error'); return; }
  // v136.8: an overnight finish longer than the 12-hour open-shift ceiling is usually an AM/PM slip (09:00 in,
  // 08:00 out reads as 23 hours). Ask rather than refuse: a genuine long night can still be saved.
  if (outResolved.overnight) {
    const hrs = overnightHours(clockIn, clockOut);
    if (hrs > MAX_OPEN_SHIFT_HOURS) {
      const ask = window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); };
      const sure = await ask('Clock-out ' + clockOut + ' is earlier than clock-in ' + clockIn + ', so it will be saved on the next day: a ' + hrs + '-hour shift.\n\n' +
        'Is that right? If you meant the evening, use the 24-hour clock (8pm is 20:00).', { confirmLabel: 'Save ' + hrs + ' hours' });
      if (!sure) return;
    }
  }
  const clockOutTs   = outResolved.ts;
  // v136.8: a break taken after midnight on an overnight shift goes on the next day too (the till's rule).
  const breakStartTs = breakStart ? clockOutDate(dateStr, clockIn, breakStart) + 'T' + breakStart + ':00' : null;
  const noteText     = 'Amended by ' + currentAdmin.name + ' (' + todayDateStr() + '): ' + reason;

  // Disable the button to prevent double-submit
  const btn = document.querySelector('#clockEditModal .btn-primary');
  const prevLabel = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

  try {
    // Upsert: with the UNIQUE (staff_id, date) constraint, this replaces
    // any existing row for this staff+date in one call.
    // Break data now editable directly (was previously preserved from
    // existing row; admins can now correct break times/minutes too).
    await DB.clock.createAdmin({
      staff_id: staffId,
      store: storeName,
      date: dateStr,
      clock_in: clockInTs,
      clock_out: clockOutTs,
      break_mins: breakMins,
      break_start: breakStartTs,
      notes: noteText,
      seq: clockEditingSeq || 1,   // v130.13 — amend the session that was opened
    });
  } catch(e) {
    console.error('[DB] Clock edit failed:', e);
    if (btn) { btn.disabled = false; btn.textContent = prevLabel; }
    showToast('Save failed: ' + (e.message || 'unknown error'), 'error');
    return;
  }

  // Log in changelog (local only — the changes tab is not DB-backed yet)
  changeLog.unshift({
    id: Date.now() + Math.random(),
    staffId: staffId,
    type: 'clock_amendment',
    from: {},
    to: { clockIn, clockOut, date: dateStr, reason },
    effectiveFrom: dateStr,
    scheduledBy: currentAdmin.name,
    appliedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  });

  if (btn) { btn.disabled = false; btn.textContent = prevLabel; }
  closeClockEditModal();
  showToast(`Clock record updated for ${s ? s.name.split(' ')[0] : 'staff'}` +
    (outResolved.overnight ? ' — overnight shift, clock-out saved on the next day' : ''), 'success');
  renderClockLog();
  updateChangesBadge();
}


// ── v129.155 — 🧹 STALE OPEN SHIFTS (#1176 step 4) ───────────────────────────
//
// The remediation surface for open clock rows past the shared 12h ceiling:
// preview EVERYTHING, apply ONLY what the operator ticks. The judgement
// (rostered finish vs clock-in +8h) is server-side in clock-stale.js; this is
// deliberately a dumb list + checkboxes. Every applied close is stamped
// ESTIMATED in the row's notes and one write_audit_log row records the
// operator + ids — an invented finish time must never look like a real one.

let _stalePreview = [];

export async function staleShiftsPreview(btn) {
  const panel = document.getElementById('staleShiftsPanel');
  if (!panel) return;
  const prev = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Scanning…'; }
  try {
    const res = await DB.clock.closeStale({});
    _stalePreview = (res && res.stale) || [];
    panel.style.display = 'block';
    if (!_stalePreview.length) {
      panel.innerHTML = '<div style="padding:14px;font-size:13px">✅ No open shifts past the ' +
        '12-hour ceiling. Nothing to close.</div>';
      return;
    }
    const rows = _stalePreview.map((p) =>
      '<tr>' +
      '<td><input type="checkbox" class="stale-tick" value="' + Number(p.id) + '" checked></td>' +
      '<td translate="no">' + escapeHtml(p.staff || '') + '</td>' +
      '<td translate="no">' + escapeHtml(p.store || '') + '</td>' +
      '<td>' + escapeHtml(p.date || '') + '</td>' +
      '<td>' + escapeHtml(String(p.clock_in || '').slice(11, 16)) + '</td>' +
      '<td><b>' + escapeHtml(String(p.proposed_clock_out || '').replace('T', ' ').slice(0, 16)) + '</b></td>' +
      '<td>' + (p.method === 'rota' ? '📋 rostered finish' : '⏱ clock-in +8h') + '</td>' +
      '</tr>').join('');
    panel.innerHTML =
      '<div style="padding:12px 14px;border-bottom:1px solid var(--border);display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px">' +
        '<div style="font-size:13px"><b>🧹 ' + _stalePreview.length + ' open shift(s) past the 12h ceiling.</b> ' +
        'Untick anything you are not sure about — closes are stamped ESTIMATED in the record and audited. Nothing closes until you press the button.</div>' +
        '<button class="btn btn-outline" onclick="Uploads.clockReview.staleShiftsApply(this)">Close selected</button>' +
      '</div>' +
      '<div class="table-wrap" style="max-height:340px;overflow:auto"><table class="data-table">' +
      '<thead><tr><th></th><th>Staff</th><th>Store</th><th>Date</th><th>In</th><th>Proposed close</th><th>Method</th></tr></thead>' +
      '<tbody>' + rows + '</tbody></table></div>';
  } catch (e) {
    panel.style.display = 'block';
    panel.innerHTML = '<div style="padding:14px;font-size:13px;color:var(--danger)">Scan failed: ' +
      escapeHtml((e && e.message) || 'error') + '</div>';
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = prev; }
  }
}

export async function staleShiftsApply(btn) {
  const ids = [...document.querySelectorAll('.stale-tick:checked')].map((c) => Number(c.value));
  if (!ids.length) { showToast('Nothing ticked — nothing closed', 'error'); return; }
  if (!confirm('Close ' + ids.length + ' open shift(s) with ESTIMATED finish times?\n\n' +
    'Each record keeps a note saying the time was estimated and by whom. This cannot be untangled in bulk — untick anything doubtful first.')) return;
  const prev = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Closing…'; }
  try {
    const res = await DB.clock.closeStale({ apply: true, ids });
    showToast('Closed ' + (res.applied || 0) + ' of ' + ids.length +
      ((res.failed && res.failed.length) ? (' — ' + res.failed.length + ' failed') : ''), 'success');
    await staleShiftsPreview(null);   // re-scan: applied rows drop out of the list
    renderClockLog();                 // the log below now shows the closed rows
  } catch (e) {
    showToast('Close failed: ' + ((e && e.message) || 'error'), 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = prev; }
  }
}
